import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import preferences from 'Preferences';
import {GridSortOptionsPreferences} from 'samples/grids/GridCellTableConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {ScrollableTableColumn, SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';
import {capitalize} from 'util/NameUtil';

/** @package */
export interface GridSortItem {
  sortKey: string;
  reverse: boolean;
  sortKeyDisplayName: string;
  sortIcon?: BenchlingIconName;
}

/** @package */
export interface CoordinatesOrderingItem {
  sortKey: string;
  orderKey: string;
  orderKeyDisplayName: string;
  orderIcon?: BenchlingIconName;
}

export type HandleGridSortByCoordinates = ({
  tableKey,
  sortKey,
  newCoordinatesOrderingKey,
  newSortDirection,
}: {
  tableKey: string;
  sortKey: string;
  newCoordinatesOrderingKey?: string;
  newSortDirection?: SortDirection;
}) => void;

/** @package */
export interface GridSortConfig {
  tableKey: string;
  availableCoordinateOrderOptions: Array<CoordinatesOrderingItem>;
  availableSortOptions: Array<GridSortItem>;
}

export interface GridSortOptionsProps {
  gridSortConfig: GridSortConfig;
  onGridSort: HandleGridSortByCoordinates;
  iconClassNames: string;
  column: ScrollableTableColumn;
}

function handleSetSortDirection(
  onGridSort: HandleGridSortByCoordinates,
  tableKey: string,
  sortKey: string,
  newSortDirection: SortDirection
): void {
  onGridSort({
    tableKey,
    sortKey,
    newSortDirection,
  });
}

function handleSetOrder(
  onGridSort: HandleGridSortByCoordinates,
  tableKey: string,
  sortKey: string,
  newCoordinatesOrderingKey: string
): void {
  onGridSort({
    tableKey,
    sortKey,
    newCoordinatesOrderingKey,
  });
}

export function getOrderMenuSection(
  availableCoordinateOrderOptions: Array<CoordinatesOrderingItem>,
  onGridSort: HandleGridSortByCoordinates,
  optionIconClassNames: string,
  tableKey: string
): JSX.Element {
  const orderItemIsSelected = (orderItem: CoordinatesOrderingItem): boolean =>
    preferences.get(`${tableKey}.${GridSortOptionsPreferences.COORDINATES_ORDER_BY_KEY}`) ===
    orderItem.orderKey;
  return (
    <Section>
      {availableCoordinateOrderOptions.map((orderItem) => (
        <Option
          value={{orderKey: orderItem.orderKey}}
          onSelect={() => {
            handleSetOrder(onGridSort, tableKey, orderItem.sortKey, orderItem.orderKey);
          }}
          key={`sort_${orderItem.orderKey}`}
          isSelected={orderItemIsSelected(orderItem)}
        >
          {orderItem.orderIcon && (
            <BenchlingIcon
              icon={orderItem.orderIcon}
              className={classNames(optionIconClassNames, {
                'is-sorted': orderItemIsSelected(orderItem),
              })}
            />
          )}
          {orderItem.orderKeyDisplayName}
        </Option>
      ))}
    </Section>
  );
}

export function getSortMenuSection(
  availableSortOptions: Array<GridSortItem>,
  onGridSort: HandleGridSortByCoordinates,
  optionIconClassNames: string,
  tableKey: string
): JSX.Element {
  const sortItemIsSelected = (sortItem: GridSortItem): boolean =>
    preferences.get(`${tableKey}.${GridSortOptionsPreferences.COORDINATES_SORT_DIRECTION}`) ===
    (sortItem.reverse ? SortDirection.DESCENDING_SORT : SortDirection.ASCENDING_SORT);
  return (
    <Section>
      {availableSortOptions.map((sortItem) => (
        <Option
          value={{sortKey: sortItem.sortKey, reverse: sortItem.reverse}}
          onSelect={() => {
            handleSetSortDirection(
              onGridSort,
              tableKey,
              sortItem.sortKey,
              sortItem.reverse ? SortDirection.DESCENDING_SORT : SortDirection.ASCENDING_SORT
            );
          }}
          key={`sort_${sortItem.sortKey}_${sortItem.reverse}`}
          isSelected={sortItemIsSelected(sortItem)}
        >
          {sortItem.sortIcon && (
            <BenchlingIcon
              icon={sortItem.sortIcon}
              className={classNames(optionIconClassNames, {
                'is-sorted': sortItemIsSelected(sortItem),
              })}
            />
          )}
          {sortItem.sortKeyDisplayName}
        </Option>
      ))}
    </Section>
  );
}

/**
 * A dropdown for sort and order options
 */
export default function GridSortOptions(props: GridSortOptionsProps): JSX.Element {
  const optionIconClassNames = classNames(
    'scrollableTable-icon scrollableTable-headerCell-rightIcon',
    'scrollableTable-headerCell-sort',
    css(styles.optionIcon)
  );

  const dropdown = (
    <Dropdown
      className="_sort-order-settings-dropdown"
      menu={
        <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.MEDIUM}>
          {getSortMenuSection(
            props.gridSortConfig.availableSortOptions,
            props.onGridSort,
            optionIconClassNames,
            props.gridSortConfig.tableKey
          )}
          {getOrderMenuSection(
            props.gridSortConfig.availableCoordinateOrderOptions,
            props.onGridSort,
            optionIconClassNames,
            props.gridSortConfig.tableKey
          )}
        </Menu>
      }
    >
      <BenchlingIcon className={props.iconClassNames} icon="triangle-down" />
    </Dropdown>
  );

  if (props.column.sortDirection != null) {
    const coordinatesOrderingKey = preferences.get(
      `${props.gridSortConfig.tableKey}.${GridSortOptionsPreferences.COORDINATES_ORDER_BY_KEY}`
    );
    const orderText = coordinatesOrderingKey ? `, ordered by ${coordinatesOrderingKey}` : '';
    return (
      <Tooltip
        container="body"
        placement={TooltipPlacement.TOP}
        title={`Sorting by ${props.column.header} (${capitalize(props.column.sortDirection)}${orderText})`}
      >
        {dropdown}
      </Tooltip>
    );
  }
  return dropdown;
}

const styles = StyleSheet.create({
  optionIcon: {
    marginLeft: '-7px',
    marginRight: '7px',
    marginBottom: '-2px',
    paddingTop: '4px',
  },
});
